import { useEffect, useState, useCallback } from 'react';
import { useSocket } from './useSocket';
import { ShotData } from '@/features/shot-visualization/types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from './use-toast';

interface UseLiveShotsOptions {
  sessionId?: string;
  autoJoin?: boolean;
  maxShots?: number;
  onNewShot?: (shot: ShotData) => void;
  onBulkShots?: (shots: ShotData[]) => void;
}

/**
 * Hook for real-time shot monitoring via WebSocket
 */
export const useLiveShots = (options: UseLiveShotsOptions = {}) => {
  const {
    sessionId,
    autoJoin = true,
    maxShots = 50,
    onNewShot,
    onBulkShots,
  } = options;

  const [liveShots, setLiveShots] = useState<ShotData[]>([]);
  const [lastShot, setLastShot] = useState<ShotData | null>(null);
  const [isLive, setIsLive] = useState(false);
  const queryClient = useQueryClient();
  
  const { socket, isConnected, joinSession, leaveSession } = useSocket({
    autoConnect: true,
  });

  // Join session room for real-time updates
  useEffect(() => {
    if (isConnected && sessionId && autoJoin) {
      joinSession(sessionId);
      setIsLive(true);
      
      toast({
        title: 'Live Mode Active',
        description: 'Real-time shot updates enabled',
      });

      return () => {
        leaveSession(sessionId);
        setIsLive(false);
      };
    }
  }, [isConnected, sessionId, autoJoin, joinSession, leaveSession]);

  // Listen for shot events
  useEffect(() => {
    if (!socket) return;

    // Handle new single shot
    const handleNewShot = (data: { shot: ShotData; sessionId?: string }) => {
      console.log('New shot received:', data);

      const shot = data.shot;
      if (!shot) return;

      // Update local state
      setLastShot(shot);
      setLiveShots(prev => {
        const updated = [shot, ...prev];
        return updated.slice(0, maxShots);
      });

      // Invalidate queries to refresh data
      if (data.sessionId) {
        queryClient.invalidateQueries({ queryKey: ['shots', 'session', data.sessionId] });
      }
      queryClient.invalidateQueries({ queryKey: ['shots', 'recent'] });

      // Call custom handler
      if (onNewShot) {
        onNewShot(shot);
      }

      // Show notification
      toast({
        title: 'New Shot',
        description: `${shot.shotType} - ${shot.distance.toFixed(1)}m (${shot.quality})`,
      });
    };

    // Handle bulk shots upload
    const handleBulkShots = (data: { shots?: ShotData[]; count?: number }) => {
      console.log('Bulk shots received:', data);

      // Invalidate all shot queries
      queryClient.invalidateQueries({ queryKey: ['shots'] });

      if (data.shots && Array.isArray(data.shots)) {
        setLiveShots(prev => {
          const updated = [...data.shots!, ...prev];
          return updated.slice(0, maxShots);
        });

        if (onBulkShots) {
          onBulkShots(data.shots);
        }
      }

      toast({
        title: 'Shots Updated',
        description: `${data.count || data.shots?.length || 0} new shots added`,
      });
    };

    // Handle session start
    const handleSessionStart = (data: { session?: { title?: string } }) => {
      console.log('Session started:', data);
      setIsLive(true);
      setLiveShots([]); // Clear shots for new session

      toast({
        title: 'Session Started',
        description: data.session?.title || 'Live monitoring active',
      });
    };

    // Handle session end
    const handleSessionEnd = (data: { shotCount?: number }) => {
      console.log('Session ended:', data);
      setIsLive(false);

      toast({
        title: 'Session Ended',
        description: `Total shots: ${data.shotCount || liveShots.length}`,
      });
    };

    // Register event listeners
    socket.on('shot:recorded', handleNewShot);
    socket.on('shot:new', handleNewShot); // Alternative event name
    socket.on('shots:bulk-uploaded', handleBulkShots);
    socket.on('session:started', handleSessionStart);
    socket.on('session:ended', handleSessionEnd);

    // Cleanup
    return () => {
      socket.off('shot:recorded', handleNewShot);
      socket.off('shot:new', handleNewShot);
      socket.off('shots:bulk-uploaded', handleBulkShots);
      socket.off('session:started', handleSessionStart);
      socket.off('session:ended', handleSessionEnd);
    };
  }, [socket, queryClient, maxShots, onNewShot, onBulkShots, liveShots.length]);

  // Manual shot simulation for testing
  const simulateShot = useCallback(() => {
    const mockShot: ShotData = {
      sessionId: sessionId || 'test-session',
      playerId: 'test-player',
      tenantId: 'test-tenant',
      timestamp: new Date().toISOString(),
      shotType: ['straight-drive', 'cover-drive', 'pull-shot', 'cut-shot'][Math.floor(Math.random() * 4)],
      quality: (['excellent', 'good', 'fair', 'poor'] as const)[Math.floor(Math.random() * 4)],
      ballSpeed: 30 + Math.random() * 10,
      batSpeed: 30 + Math.random() * 15,
      exitVelocity: 40 + Math.random() * 20,
      launchAngle: 5 + Math.random() * 20,
      horizontalAngle: -45 + Math.random() * 90,
      distance: 40 + Math.random() * 40,
      height: 2 + Math.random() * 10,
      spinRate: 1500 + Math.random() * 1500,
      impactLocationX: -0.05 + Math.random() * 0.1,
      impactLocationY: 0.1 + Math.random() * 0.1,
      sweetSpotDistance: Math.random() * 0.05,
      powerTransfer: 0.7 + Math.random() * 0.25,
      timing: 'good' as const,
      footwork: 'good' as const,
      balance: 'good' as const,
      followThrough: 'complete' as const,
    };

    // Emit as if received from server
    if (socket) {
      socket.emit('shot:recorded', { shot: mockShot, sessionId });
    }

    // Also update local state for immediate feedback
    setLastShot(mockShot);
    setLiveShots(prev => [mockShot, ...prev].slice(0, maxShots));

    toast({
      title: 'Shot Simulated',
      description: `${mockShot.shotType} - ${mockShot.distance.toFixed(1)}m`,
    });
  }, [socket, sessionId, maxShots]);

  // Clear all shots
  const clearShots = useCallback(() => {
    setLiveShots([]);
    setLastShot(null);
  }, []);

  return {
    liveShots,
    lastShot,
    isLive,
    isConnected,
    simulateShot,
    clearShots,
    shotCount: liveShots.length,
  };
};